import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
} from 'firebase/firestore';
import { firestore as db } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import moment from 'moment';
import { FaTimes, FaHeart, FaRegHeart, FaFlag, FaEye, FaChartLine } from 'react-icons/fa';
import PlayerProfileModal from './PlayerProfileModal';
import { Carousel, Button } from 'react-bootstrap';
import ReportDialog from './safety/ReportDialog';
import { GENERAL_REPORT_REASONS, reportFeedPost } from '../services/safetyService';
import './PostCard.css';

const isRaceCategory = (category = '') => /race|match|league|tournament/i.test(category);

export default function PostCard({ post, onClose, onRequireAuth, disableProfileOpen = false }) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [isLiked, setIsLiked] = useState(post.postlikes?.includes(uid));
  const [isDisliked, setIsDisliked] = useState(post.postdislikes?.includes(uid));
  const [likeCount, setLikeCount] = useState(post.postlikes?.length || 0);
  const [dislikeCount, setDislikeCount] = useState(post.postdislikes?.length || 0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [profileData, setProfileData] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const formattedTime = post.timestamp?.toDate
    ? moment(post.timestamp.toDate()).fromNow()
    : '';

  const postRef = doc(db, 'feedPost', post.id);
  const imageUrls = [post.image1, post.image2, post.image3].filter(Boolean);
  const tags = Array.isArray(post.tags) ? post.tags.filter(Boolean).slice(0, 5) : [];

  const displayName = post.isBusinessPost
    ? profileData?.businessBrand || post.nickname || 'Pool Business'
    : profileData?.nickname || post.nickname || 'Pool Player';

  const profileImageUrl = profileData?.profileImageUrl || post.userImage || 'https://placehold.co/120x120/111111/FFD45C?text=P4R';

  useEffect(() => {
    setIsLiked(post.postlikes?.includes(uid));
    setIsDisliked(post.postdislikes?.includes(uid));
    setLikeCount(post.postlikes?.length || 0);
    setDislikeCount(post.postdislikes?.length || 0);
  }, [post.postlikes, post.postdislikes, uid]);

  useEffect(() => {
    if (!uid) return;
    const checkFavorites = async () => {
      const ref = doc(db, 'feedExperience', uid);
      const snap = await getDoc(ref);
      const favs = snap.data()?.favorites || [];
      setIsFavorite(favs.includes(post.id));
    };
    checkFavorites();
  }, [uid, post.id]);

  useEffect(() => {
    if (!post.id || viewRecorded) return;
    setViewRecorded(true);
    updateDoc(postRef, { views: increment(1) }).catch(() => {});
  }, [post.id, postRef, viewRecorded]);

  const fetchProfileData = async () => {
    if (disableProfileOpen) return;
    try {
      const ref = post.isBusinessPost && post.locationId
        ? doc(db, 'businessprofiles', post.locationId)
        : doc(db, 'userprofiles', post.userId);

      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProfileData({ id: snap.id, ...snap.data() });
        setShowProfileModal(true);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleLike = async () => {
    if (!uid) {
      onRequireAuth?.();
      return;
    }
    await updateDoc(postRef, {
      postlikes: isLiked ? arrayRemove(uid) : arrayUnion(uid),
      postdislikes: arrayRemove(uid),
      lastLikeTime: new Date(),
    });
    setIsLiked(l => !l);
    if (isDisliked) setDislikeCount(c => Math.max(0, c - 1));
    setLikeCount(c => Math.max(0, c + (isLiked ? -1 : 1)));
    setIsDisliked(false);
  };

  const handleDislike = async () => {
    if (!uid) {
      onRequireAuth?.();
      return;
    }
    await updateDoc(postRef, {
      postdislikes: isDisliked ? arrayRemove(uid) : arrayUnion(uid),
      postlikes: arrayRemove(uid),
      lastDislikeTime: new Date(),
    });
    setIsDisliked(d => !d);
    if (isLiked) setLikeCount(c => Math.max(0, c - 1));
    setDislikeCount(c => Math.max(0, c + (isDisliked ? -1 : 1)));
    setIsLiked(false);
  };

  const toggleFavorite = async () => {
    if (!uid) {
      onRequireAuth?.();
      return;
    }
    const ref = doc(db, 'feedExperience', uid);
    await setDoc(ref, {
      favorites: isFavorite ? arrayRemove(post.id) : arrayUnion(post.id),
      timeUpdated: new Date(),
    }, { merge: true });
    setIsFavorite(prev => !prev);
  };

  const signalPercent = useMemo(() => {
    const total = likeCount + dislikeCount;
    const likeRate = total > 0 ? likeCount / total : 0.5;
    const engageRate = post.views > 0 ? Math.min(1, total / post.views) : 0;
    return Math.round((likeRate * 0.75 + engageRate * 0.25) * 100);
  }, [likeCount, dislikeCount, post.views]);

  const postText = post.postText || '';
  const shouldTruncate = postText.length > 180;
  const visibleText = showFullText || !shouldTruncate ? postText : `${postText.slice(0, 180)}…`;

  return (
    <>
      <article className="pc2-card p4r-modern-post-card">
        {onClose && (
          <button className="pc2-close-btn" onClick={onClose} aria-label="Close post">
            <FaTimes />
          </button>
        )}

        <header className="p4r-post-head">
          <button className="p4r-post-author" onClick={fetchProfileData} type="button" disabled={disableProfileOpen} aria-disabled={disableProfileOpen}>
            <img src={profileImageUrl} alt={displayName} />
            <span>
              <strong>{displayName}</strong>
              <small>{formattedTime || 'Recently'} {post.isBusinessPost ? '• Business' : ''}</small>
            </span>
          </button>
          <span className="p4r-post-category">{post.category || 'Pockit'}</span>
        </header>

        {imageUrls.length > 0 && (
          <div className={`p4r-post-media-grid count-${imageUrls.length}`}>
            {imageUrls.map((url, index) => (
              <button
                key={url}
                type="button"
                onClick={() => {
                  setFullscreenIndex(index);
                  setShowFullscreen(true);
                }}
              >
                <img src={url} alt="Post media" />
              </button>
            ))}
          </div>
        )}

        <div className="p4r-post-body">
          <h3>{post.postTitle}</h3>
          <p>
            {visibleText}
            {shouldTruncate && (
              <button type="button" className="pc2-toggle-text" onClick={() => setShowFullText(f => !f)}>
                {showFullText ? ' Show Less' : ' Read More'}
              </button>
            )}
          </p>

          {tags.length > 0 && (
            <div className="p4r-post-tags">
              {tags.map(tag => <span key={tag}>#{tag}</span>)}
            </div>
          )}

          {isRaceCategory(post.category) && (
            <div className="p4r-post-race-cta">
              <strong>Race</strong>
              {uid ? (
                <Link to="/race-room">Open</Link>
              ) : (
                <button type="button" onClick={() => onRequireAuth?.()}>Open</button>
              )}
            </div>
          )}
        </div>

        <footer className="pc2-actions-center p4r-post-actions">
          <Button variant={isLiked ? 'info' : 'outline-info'} size="sm" onClick={handleLike}>
            👍 {likeCount}
          </Button>
          <Button variant={isDisliked ? 'danger' : 'outline-danger'} size="sm" onClick={handleDislike}>
            👎 {dislikeCount}
          </Button>
          <Button variant="outline-warning" size="sm" onClick={toggleFavorite} aria-label="Favorite post">
            {isFavorite ? <FaHeart /> : <FaRegHeart />}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => uid ? setShowReport(true) : onRequireAuth?.()}
            aria-label="Report post"
          >
            <FaFlag />
          </Button>
          <span className="p4r-post-mini-stat" title="Post reach"><FaEye /> {Number(post.views || 0)}</span>
          <span className="p4r-post-mini-stat" title="Positive engagement"><FaChartLine /> {signalPercent}%</span>
        </footer>

        <div className="pc2-signal-wrapper">
          <div className="pc2-signal-bar" style={{ width: `${signalPercent}%` }} />
        </div>
      </article>

      {showFullscreen && (
        <div className="pc2-fs-overlay" onClick={() => setShowFullscreen(false)}>
          <button
            className="pc2-fs-close-btn"
            onClick={e => {
              e.stopPropagation();
              setShowFullscreen(false);
            }}
            aria-label="Close fullscreen"
          >
            <FaTimes size={24} />
          </button>

          <div className="pc2-fs-carousel-container" onClick={e => e.stopPropagation()}>
            <Carousel
              activeIndex={fullscreenIndex}
              onSelect={i => setFullscreenIndex(i)}
              interval={null}
              prevIcon={<span className="pc2-carousel-control-icon pc2-prev-icon">‹</span>}
              nextIcon={<span className="pc2-carousel-control-icon pc2-next-icon">›</span>}
            >
              {imageUrls.map((url, idx) => (
                <Carousel.Item key={idx}>
                  <img src={url} alt="Post media fullscreen" className="pc2-fs-img" />
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </div>
      )}

      <ReportDialog
        show={showReport}
        onClose={() => setShowReport(false)}
        title="Report Pockit"
        reasons={GENERAL_REPORT_REASONS}
        onSubmit={(reason, otherReason) => reportFeedPost({ uid, post, reason, otherReason })}
      />

      {!disableProfileOpen && profileData && (
        <PlayerProfileModal
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          player={profileData}
        />
      )}
    </>
  );
}
